import { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Plus as PlusIcon } from 'react-feather'
import { useTheme } from '@material-ui/styles'
import { Box, Button, TablePagination, useMediaQuery } from '@material-ui/core'

import {
  Page,
  Container,
  ContentHeader,
  LoadingFeedback,
  BackButton,
  Card,
  ConfirmationDialog,
  Permitted,
} from 'components'
import {
  RegulationForm,
  RegulationVersionsTable,
  VersionModal,
} from './components'

import helpers from 'helpers'

import { useFetch, usePagination, useSnackbar } from 'hooks'

import constants from 'constants/index'
import * as service from 'service'

const RegulationsEdit = ({ match }) => {
  const [openShowModal, setOpenShowModal] = useState(false)
  const [openCreateModal, setOpenCreateModal] = useState(false)
  const [openInactivateDialog, setOpenInactivateDialog] = useState(false)
  const [selectedVersion, setSelectedVersion] = useState(null)
  const [saving, setSaving] = useState(false)

  const { regulationId } = match.params

  const { STATUSES, FORM_TYPES } = constants.regulations
  const { REGULATIONS } = constants.permissions

  const theme = useTheme()
  const snackbar = useSnackbar()
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'), {
    defaultMatches: true,
  })

  const generalRef = useRef()

  const { perPage, page, handleChangePage, handleChangeRowsPerPage } =
    usePagination(5)

  const { response, isLoading, refresh } = useFetch(
    service.backoffice.regulations.get,
    { regulationId },
    [regulationId],
  )

  const {
    response: versionsResponse,
    isLoading: isVersionsLoading,
    refresh: refreshVersions,
  } = useFetch(
    service.backoffice.regulationVersions.get,
    { regulationId, perPage, page },
    [regulationId, perPage, page],
  )

  const regulation = response?.data?.regulation
  const regulationVersions = versionsResponse?.data?.regulationVersions || []
  const totalVersionsCount = versionsResponse?.data?.meta?.totalCount || 0

  const isInactiveRegulation = regulation?.status === STATUSES.INACTIVE

  const loading = isLoading || isVersionsLoading || saving

  const handleRefresh = () => {
    refresh()
    refreshVersions()
  }

  const handleOpenCreateModal = () => setOpenCreateModal(true)

  const handleOpenShowModal = (regulationVersionId) => {
    const regulationVersion = regulationVersions.find(
      (regulationVersion) => regulationVersion?.id === regulationVersionId,
    )

    setSelectedVersion(regulationVersion)
    setOpenShowModal(true)
  }

  const handleOpenInactivateDialog = (regulationVersionId) => {
    const regulationVersion = regulationVersions.find(
      (regulationVersion) => regulationVersion?.id === regulationVersionId,
    )

    setSelectedVersion(regulationVersion)
    setOpenInactivateDialog(true)
  }

  const handleInactivate = async () => {
    try {
      setSaving(true)

      await service.backoffice.regulationVersions.changeStatus({
        id: selectedVersion?.id,
        regulationId,
        status: STATUSES.INACTIVE,
      })

      snackbar.open({
        message: 'Versão inativada com sucesso!',
        variant: 'success',
      })

      handleRefresh()
      setOpenInactivateDialog(false)
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Falha ao inativar versão!',
        variant: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  const handleSave = async () => {
    const generalData = await generalRef.current.submit()

    if (!generalData) return

    try {
      setSaving(true)
      await service.backoffice.regulations.update({
        regulationId,
        ...generalData,
      })

      snackbar.open({
        message: 'Regulamento atualizado com sucesso!',
        variant: 'success',
      })

      refresh()
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(
            error?.response?.data?.error,
            false,
          ) || 'Falha ao atualizar regulamento!',
        variant: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Page title="Editar regulamento">
      <LoadingFeedback open={saving} />
      <Container
        maxWidth={false}
        spacingXl={30}
        spacingLg={25}
        spacingMd={10}
        spacingSm={10}
      >
        <ContentHeader
          title="Editar"
          menu="Documentos"
          subtitle="Editar regulamento"
        />
        <Box display="flex" flexDirection="column" gridGap={theme.spacing(4)}>
          <RegulationForm
            ref={generalRef}
            type={FORM_TYPES.EDIT}
            regulation={regulation}
            isLoading={isLoading}
            key={isLoading ? 'edit-regulation-loading' : 'edit-regulation'}
          />
          <Box>
            <Card
              title="Versões"
              fromTable
              withPagination
              actionButton={
                !isInactiveRegulation && (
                  <Permitted tag={REGULATIONS.MANAGE}>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<PlusIcon />}
                      onClick={handleOpenCreateModal}
                    >
                      Criar
                    </Button>
                  </Permitted>
                )
              }
            >
              <RegulationVersionsTable
                regulationVersions={regulationVersions}
                isLoading={isVersionsLoading}
                onShow={handleOpenShowModal}
                onInactive={handleOpenInactivateDialog}
              />
              <Box px={2} display="flex" justifyContent="flex-end">
                <TablePagination
                  component="div"
                  count={totalVersionsCount}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  page={page - 1}
                  rowsPerPage={perPage}
                  rowsPerPageOptions={[5, 10, 25, 100]}
                  labelRowsPerPage={isDesktop ? 'Por página' : ''}
                  nextIconButtonProps={{ size: 'small' }}
                  backIconButtonProps={{ size: 'small' }}
                />
              </Box>
            </Card>
          </Box>
          <VersionModal
            open={openCreateModal}
            setOpen={setOpenCreateModal}
            regulationId={regulationId}
            refresh={handleRefresh}
          />
          <VersionModal
            open={openShowModal}
            setOpen={setOpenShowModal}
            type={FORM_TYPES.SHOW}
            regulationVersion={selectedVersion}
          />
          <ConfirmationDialog
            open={openInactivateDialog}
            setOpen={setOpenInactivateDialog}
            message="Deseja realmente inativar esta versão?"
            cancelButtonText="cancelar"
            buttonText="Inativar"
            actionAcceptButton={handleInactivate}
          />
          <Box display="flex" gridGap={theme.spacing(1)}>
            <BackButton />
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={loading}
            >
              Salvar
            </Button>
          </Box>
        </Box>
      </Container>
    </Page>
  )
}

RegulationsEdit.propTypes = {
  match: PropTypes.object,
}

export default RegulationsEdit
